import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DotLottleLoader } from '../../../components/Loader';
import axiosInstant from '../../../api';
import { useUser } from '../../../hooks/useUser';
import ExpertSelectionCard from '../../videoProduction/components/ExpertSelectionCard';
import { IBrandExpert } from '../../brand/expertsV2/expertListView/constants';
import { ReactComponent as AddIcon } from '../../../assets/svg/add-icon.svg';
import { ReactComponent as NoExpertsIcon } from '../../../assets/svg/no-experts.svg';
import { usePortalPath } from '../../../hooks/usePortalPath'

// Extend IBrandExpert to include our API data structure
interface Expert extends IBrandExpert {
  expertise?: Array<{
    userId: number;
    expertise: string;
  }>;
  keyPoints?: Array<{
    userId: number;
    keyPoint: string;
  }>;
}

interface ExpertsProps {
  limit?: number;
  onCount?: (count: number) => void;
}

const Experts: React.FC<ExpertsProps> = ({ limit = 8, onCount }) => {
  const navigate = useNavigate();
  const { p } = usePortalPath();
  const { user } = useUser();
  const [experts, setExperts] = useState<Expert[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const fetchExperts = async () => {
    try {
      setLoading(true);

      // Use the specific API call format provided
      const brandId = user?.brand?.[0]?.id || 35; // Default to 35 if no brand ID
      const response = await axiosInstant.get(
        `/brands/${brandId}/pros?page=0&limit=${limit}`,
      );

      if (response.data?.rows) {
        setExperts(response.data.rows);
        setTotalCount(response.data.count || 0);
        onCount?.(response.data.count || 0);
      } else if (response.data?.data) {
        setExperts(response.data.data);
        setTotalCount(response.data.count || 0);
        onCount?.(response.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching experts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchExperts();
    }
  }, [limit, user]);

  const handleViewAll = () => {
    navigate(p('/experts'));
  };

  const handleViewProfile = (expertId: number) => {
    navigate(p(`/experts/${expertId}`));
  };

  const handleAddExpert = () => {
    navigate(p('/experts/new'));
  };

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-full min-h-0">
        <DotLottleLoader fill={false} />
      </Box>
    );
  }

  if (experts.length === 0) {
    return (
      <Box className="flex flex-col h-full min-h-0">
        <Box className="flex items-center justify-between mb-4 flex-shrink-0">
          <Typography
            sx={{
              color: 'var(--text)',
              fontFamily: 'CircularStd',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: 'normal',
            }}
          >
            Experts
          </Typography>
        </Box>
        <Box
          className="flex-1 flex flex-col items-center justify-center w-full"
          sx={{ gap: '21px' }}
        >
          <NoExpertsIcon />
          <Typography
            sx={{
              color: 'var(--Base-600, #727272)',
              fontFamily: '"CircularStd"',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 450,
              lineHeight: 'normal',
              textAlign: 'center',
            }}
          >
            You have no experts available
          </Typography>
          <Button
            onClick={handleAddExpert}
            sx={{
              backgroundColor: 'var(--accent)',
              color: 'var(--Base-0, #FFF)',
              fontFamily: 'CircularStd',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 900,
              lineHeight: 'normal',
              textTransform: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              alignSelf: 'center',
              '&:hover': {
                backgroundColor: '#8A6D41',
              },
            }}
          >
            <AddIcon />
            Add Expert
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="flex flex-col h-full min-h-0">
      {/* Header */}
      <Box className="flex items-center justify-between mb-4 flex-shrink-0">
        <Typography
          sx={{
            color: 'var(--text)',
            fontFamily: 'CircularStd',
            fontSize: { xs: '15px', md: '18px' },
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
          }}
        >
          Experts ({totalCount})
        </Typography>
        <Button
          onClick={handleViewAll}
          sx={{
            color: 'var(--accent-text)',
            fontFamily: 'CircularStd',
            fontSize: { xs: '12px', md: '14px' },
            fontWeight: 450,
            textTransform: 'none',
            padding: 0,
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          View All
        </Button>
      </Box>

      {/* Experts List - Horizontal Scroll */}
      <Box className="flex-1 min-h-0 overflow-x-auto pb-0">
        <Box className="flex gap-6 h-full max-md:gap-4" style={{ width: 'max-content' }}>
          {experts.map((expert) => (
            <div
              key={expert.userid}
              className="flex-shrink-0 w-[300px] max-md:!w-[230px] [&>div]:max-md:!min-w-0"
            >
              <ExpertSelectionCard
                isSelected={false}
                recommendationCheck={false}
                pro={expert}
                onUpdate={() => {}}
                project={null}
                onScoreChange={() => {}}
                approvalCheck={false}
                isTopRecommendation={false}
                isApprovalPage={false}
                hideKeyPoints={true}
                hideSelection={true}
                customHeight={302 as any}
              />
            </div>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Experts;
